'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X as XIcon, Loader2, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/shadcn/button';

interface AdaptiveDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  onSave: () => void;
  saveEnabled?: boolean;
  isSaving?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  onDelete?: () => void;
  deleteEnabled?: boolean;
  isDeleting?: boolean;
  deleteLabel?: string;
  children: React.ReactNode;
  contentClassName?: string;
}

/**
 * Adaptive dialog that mirrors the Apple platform conventions:
 * - < sm (640px, mobile-like viewport): full-screen sheet with a top bar —
 *   `X` (close) on the left, centered title, `Save` on the right.
 *   Destructive action (when provided) renders as a full-width inline button
 *   at the bottom of the scrollable content (iOS-style).
 * - >= sm (tablet / desktop): centered basic dialog with title at the top,
 *   scrollable content, and a footer with the destructive action on the
 *   bottom-left and Cancel / Save on the bottom-right (macOS-style).
 */
export function AdaptiveDialog({
  open,
  onClose,
  title,
  onSave,
  saveEnabled = true,
  isSaving = false,
  saveLabel,
  cancelLabel,
  onDelete,
  deleteEnabled = true,
  isDeleting = false,
  deleteLabel,
  children,
  contentClassName,
}: AdaptiveDialogProps) {
  const tCommon = useTranslations('common');
  const save = saveLabel ?? tCommon('save');
  const cancel = cancelLabel ?? tCommon('cancel');
  const del = deleteLabel ?? tCommon('delete');

  const saveDisabled = !saveEnabled || isSaving || isDeleting;
  const deleteDisabled = !deleteEnabled || isDeleting || isSaving;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        {/* Overlay — soft gray dim (no blur), mirrors Apple sheet backdrop. */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-foreground/45',
            'transition-opacity duration-200 ease-in-out',
            'data-[state=open]:opacity-100 data-[state=closed]:opacity-0'
          )}
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed z-50 flex flex-col bg-background outline-none',
            // Mobile: full-screen sheet
            'inset-0 w-full h-full rounded-none',
            // Tablet / desktop: centered card with soft, borderless Apple-like surface
            'sm:inset-auto sm:top-[50%] sm:left-[50%]',
            'sm:-translate-x-1/2 sm:-translate-y-1/2',
            'sm:w-full sm:max-w-lg sm:max-h-[90vh] sm:h-auto',
            'sm:rounded-[28px] sm:shadow-[0_40px_240px_-20px_rgba(0,0,0,0.22),0_8px_32px_-12px_rgba(0,0,0,0.08)]',
            // Transitions
            'transition-all duration-200 ease-in-out',
            'data-[state=open]:opacity-100 data-[state=open]:scale-100',
            'data-[state=closed]:opacity-0 sm:data-[state=closed]:scale-95',
            contentClassName
          )}
        >
          {/* Mobile top bar */}
          <div className="flex sm:hidden items-center justify-between gap-2 border-b px-2 py-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-accent"
              aria-label={tCommon('close')}
            >
              <XIcon className="w-5 h-5" />
            </button>
            <DialogPrimitive.Title className="flex-1 text-center text-list-item-title truncate">
              {title}
            </DialogPrimitive.Title>
            <Button
              variant="ghost"
              onClick={onSave}
              disabled={saveDisabled}
              className="text-primary font-medium"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : save}
            </Button>
          </div>

          {/* Desktop title */}
          <div className="hidden sm:block px-7 pt-7 pb-5">
            <DialogPrimitive.Title className="text-sheet-title">
              {title}
            </DialogPrimitive.Title>
          </div>

          {/* Scrollable content. Mobile keeps `flex-1` to fill the sheet,
              desktop uses `sm:flex-none` so the dialog hugs its content
              (no empty whitespace below short inputs). `min-h-0` lets the
              flex item shrink and scroll when content exceeds `max-h`. */}
          <div className="flex-1 sm:flex-none min-h-0 overflow-y-auto px-4 sm:px-7 pb-5">
            {children}

            {/* Mobile-only inline destructive action */}
            {onDelete && (
              <div className="sm:hidden pt-4">
                <Button
                  variant="ghost"
                  onClick={onDelete}
                  disabled={deleteDisabled}
                  className="w-full text-destructive hover:text-destructive"
                >
                  {isDeleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      {del}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Desktop footer */}
          <div className="hidden sm:flex items-center gap-2 border-t px-7 py-5">
            {onDelete ? (
              <Button
                variant="ghost"
                onClick={onDelete}
                disabled={deleteDisabled}
                className="text-destructive hover:text-destructive"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    {del}
                  </>
                )}
              </Button>
            ) : null}
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                {cancel}
              </Button>
              <Button onClick={onSave} disabled={saveDisabled}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : save}
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
